import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import {
  getScheduleDocumentsByScheduleIdAPI,
  createScheduleDocumentAPI,
  updateScheduleDocumentAPI,
  deleteScheduleDocumentAPI,
} from "../../../services/portal/admin/documentService";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../../utility/ToastMessage";
import Loader from "../../../components/Loader";
import RemoveConfirmationDialog from "../../../components/portal/RemoveConfirmationDialog";
import { scheduleDate, tableHeaderColor } from "../../../utility/Common";
import { RoutePaths } from "../../../utility/RoutePaths";
import { getScheduleByIdAPI } from "../../../services/portal/admin/classService";

const MAX_PDF_BYTES = 10 * 1024 * 1024;

const ScheduleDocumentManagePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const classId = searchParams.get("classId");
  const scheduleId = searchParams.get("scheduleId");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [formData, setFormData] = useState({ title: "", description: "" });
  const [pdfFile, setPdfFile] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "" });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [scheduleDetails, setScheduleDetails] = useState(null);

  const classIdNum = classId ? Number(classId) : 0;

  const loadScheduleDetails = async () => {
    const res = await getScheduleByIdAPI(scheduleId);
    if (res && res.IsSuccessful && res.Data) {
      setScheduleDetails(res.Data);
    } else {
      setScheduleDetails(null);
    }
  };

  const loadDocuments = useCallback(async () => {
    if (scheduleId > 0) {
      setDocuments([]);
      setLoading(true);
      try {
        const res = await getScheduleDocumentsByScheduleIdAPI(scheduleId);
        if (res && res.IsSuccessful && Array.isArray(res.Data)) {
          setDocuments(res.Data);
        } else {
          setDocuments([]);
        }
      } catch (error) {
        console.error("Error loading schedule documents:", error);
        showErrorToastMsg("Failed to load documents");
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    }
  }, [scheduleId]);

  useEffect(() => {
    loadScheduleDetails();
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePdfChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      showErrorToastMsg("Please select a PDF file only");
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      showErrorToastMsg("PDF must be smaller than 10 MB");
      return;
    }
    setPdfFile(file);
  };

  const clearUploadForm = () => {
    setFormData({ title: "", description: "" });
    setPdfFile(null);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    const title = formData.title.trim();
    if (!title) {
      showErrorToastMsg("Please enter a title");
      return;
    }
    if (!pdfFile) {
      showErrorToastMsg("Please select a PDF file");
      return;
    }
    setSaving(true);
    try {
      const params = new FormData();
      params.append("id", "0");
      params.append("classId", String(classIdNum));
      params.append("ScheduleId", String(scheduleId));
      params.append("Title", formData.title.trim());
      params.append("Description", formData.description.trim());
      params.append("File", pdfFile);
      params.append("Status", "1");
      const res = await createScheduleDocumentAPI(params);
      if (res && res.IsSuccessful) {
        showSuccessToastMsg("Document uploaded successfully");
        clearUploadForm();
        await loadDocuments();
      } else {
        showErrorToastMsg("Something went wrong, please try again later");
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      showErrorToastMsg("Failed to upload document");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingRow(row);
    setEditForm({
      title: row.Title,
      description: row.Description,
    });
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setEditForm({ title: "", description: "" });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingRow) return;
    const id = editingRow.Id;
    const title = editForm.title.trim();
    if (!title) {
      showErrorToastMsg("Please enter a title");
      return;
    }
    setSaving(true);
    try {
      const params = new FormData();
      params.append("id", Number(id));
      params.append("Title", title);
      params.append("Description", editForm.description.trim());
      const res = await updateScheduleDocumentAPI(params);
      if (res && res.IsSuccessful) {
        showSuccessToastMsg("Document updated successfully");
        cancelEdit();
        await loadDocuments();
      } else {
        showErrorToastMsg(
          res?.Message || "Something went wrong, please try again later",
        );
      }
    } catch (error) {
      console.error("Error updating document:", error);
      showErrorToastMsg("Failed to update document");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (row) => {
    setDeleteItem(row);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    const id = deleteItem.Id;
    if (id == null) {
      showErrorToastMsg("Cannot delete: missing document id");
      setShowDeleteConfirm(false);
      setDeleteItem(null);
      return;
    }
    setSaving(true);
    try {
      const res = await deleteScheduleDocumentAPI(id);
      if (res && res.IsSuccessful) {
        showSuccessToastMsg("Document removed successfully");
        if (editingRow && editingRow.Id === id) cancelEdit();
        await loadDocuments();
      } else {
        showErrorToastMsg("Failed to remove document");
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      showErrorToastMsg("Failed to remove document");
    } finally {
      setSaving(false);
      setShowDeleteConfirm(false);
      setDeleteItem(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteItem(null);
  };

  const backToSchedules = () => {
    navigate(
      `${RoutePaths.ADMIN_CLASS_SCHEDULE_PAGE}?classId=${encodeURIComponent(classId || "")}`,
    );
  };

  return (
    <DashboardLayout
      title="Schedule documents"
      subtitle="Upload PDFs linked to this schedule"
    >
      <div className="mb-4">
        <button
          type="button"
          onClick={backToSchedules}
          className="text-sm font-medium text-primary-600 hover:text-primary-800"
        >
          ← Back to class schedules
        </button>
      </div>

      {scheduleDetails && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-primary-600">
          <div className="d-flex">
            <h2 className="text-xl font-bold text-gray-900 cursor-pointer">
              {scheduleDetails.Description}
            </h2>
          </div>
        </div>
      )}

      {loading ? (
        <Loader />
      ) : (
        <div className="space-y-8">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div
              className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
            >
              <h4 className="text-md font-medium text-white">
                {editingRow ? "Edit document" : "Upload document"}
              </h4>
            </div>
            <div className="p-6">
              {editingRow ? (
                <form onSubmit={handleEditSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={editForm.title}
                      onChange={handleEditChange}
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="description"
                      value={editForm.description}
                      onChange={handleEditChange}
                      rows={4}
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex flex-wrap gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className={`text-md font-medium px-4 py-2 rounded-md transition-colors ${
                        saving
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-primary-600 text-white hover:bg-primary-700"
                      }`}
                    >
                      {saving ? "Saving..." : "Save changes"}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={saving}
                      className="text-md font-medium px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleUploadSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Document title"
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Optional description"
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PDF file <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={handlePdfChange}
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      PDF only, maximum size 10 MB.
                    </p>
                    {pdfFile && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-gray-600">
                          Selected: {pdfFile.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => setPdfFile(null)}
                          className="text-xs text-red-600 hover:text-red-800 font-medium"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className={`text-md font-medium px-4 py-2 rounded-md transition-colors ${
                        saving
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-primary-600 text-white hover:bg-primary-700"
                      }`}
                    >
                      {saving ? "Uploading..." : "Upload"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div
              className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
            >
              <h4 className="text-md font-medium text-white">
                Uploaded documents
              </h4>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {documents.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-6 py-8 text-center text-sm text-gray-500"
                      >
                        No documents yet. Upload a PDF above.
                      </td>
                    </tr>
                  ) : (
                    documents.map((row) => {
                      return (
                        <tr key={row.Id}>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                            {row.Title}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 max-w-md">
                            <span className="line-clamp-3 whitespace-pre-wrap">
                              {row.Description}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {row.Path ? (
                              <a
                                href={row.Path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:text-primary-800 font-medium"
                              >
                                View PDF
                              </a>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => {
                                startEdit(row);
                              }}
                              className={`mr-4 text-primary-600 hover:text-primary-900`}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                handleDelete(row);
                              }}
                              className={"text-red-600 hover:text-red-900"}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <RemoveConfirmationDialog
          loading={saving}
          description={deleteItem ? deleteItem.Title : ""}
          cancelDelete={cancelDelete}
          confirmDelete={confirmDelete}
        />
      )}
    </DashboardLayout>
  );
};

export default ScheduleDocumentManagePage;
